<template>
  <div class="bg-white shadow-sm border border-slate-200 overflow-hidden">
    <div v-if="!hideHeader" class="px-6 pt-2 pb-1">
      <div class="flex items-baseline gap-2 flex-wrap">
        <h2 class="text-base font-bold text-[#0d4169] text-left">
          {{ resolvedCompanyName }}
        </h2>
        <span class="text-sm font-bold text-slate-600">Income Tax</span>
      </div>
    </div>

    <FinanceToast :show="toast.show" :variant="toast.variant">{{ toast.message }}</FinanceToast>

    <div v-if="isLoadingRows" class="px-6 py-12 flex justify-center items-center">
      <div class="h-8 w-8 border-2 border-[#0d4169]/30 border-t-transparent rounded-full animate-spin"></div>
    </div>

    <form v-else @submit.prevent="save" class="px-6 pt-2">
      <div class="grid grid-cols-1 md:grid-cols-[1fr_1fr_1fr_1.2fr_auto] items-end gap-3">
        <div class="flex flex-col gap-1">
          <label class="text-[0.78rem] font-semibold text-[#1e293b] text-left">Salary Range From</label>
          <input
            v-model.number="form.from"
            type="number"
            min="0"
            step="0.01"
            class="w-full px-2.5 py-1.5 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm"
            placeholder="From"
            required
          >
        </div>

        <div class="flex flex-col gap-1">
          <label class="text-[0.78rem] font-semibold text-[#1e293b] text-left">Upto</label>
          <input
            v-model.number="form.upto"
            type="number"
            min="0"
            step="0.01"
            class="w-full px-2.5 py-1.5 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm"
            placeholder="Upto"
            required
          >
        </div>

        <div class="flex flex-col gap-1">
          <label class="text-[0.78rem] font-semibold text-[#1e293b] text-left">Tax</label>
          <input
            v-model.number="form.tax"
            type="number"
            min="0"
            step="0.01"
            class="w-full px-2.5 py-1.5 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm"
            placeholder="Tax %"
            required
          >
        </div>

        <div class="flex flex-col gap-1">
          <label class="text-[0.78rem] font-semibold text-[#1e293b] text-left">Deduction</label>
          <input
            v-model="form.deduction"
           
            type="text"
            class="w-full px-2.5 py-1.5 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm"
            placeholder="Enter Deduction"
          >
        </div>

        <button
          v-if="editingId"
          type="submit"
          :disabled="!canEditIncomeTax || isSaving"
          class="min-w-max px-5 py-1.5 bg-[#0d4169] text-white text-sm font-bold rounded-md shadow-md hover:bg-[#0a3354] transition-all active:scale-95 whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        >
          <div
            v-if="isSaving"
            class="h-5 w-5 border-2 border-white/30 border-t-transparent rounded-full animate-spin"
          ></div>
          <span v-if="!isSaving">Update Changes</span>
        </button>
        <button
          v-else
          type="submit"
          :disabled="!canAddIncomeTax || isSaving"
          class="min-w-max px-5 py-1.5 bg-[#0d4169] text-white text-sm font-bold rounded-md shadow-md hover:bg-[#0a3354] transition-all active:scale-95 whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        >
          <div
            v-if="isSaving"
            class="h-5 w-5 border-2 border-white/30 border-t-transparent rounded-full animate-spin"
          ></div>
          <span v-if="!isSaving">Save</span>
        </button>
      </div>
    </form>

    <div class="px-6 pt-4 pb-6">
      <div class="w-full flex justify-end mb-2">
        <input
          v-model="searchQuery"
          type="text"
          class="w-full sm:w-[260px] px-2.5 pr-8 py-1 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm"
          placeholder="Search by From or Upto"
        />
        <svg
          class="w-4 h-4 text-slate-400 -ml-6 mt-[7px] pointer-events-none"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M21 21l-4.35-4.35m1.35-4.65a7 7 0 11-14 0 7 7 0 0114 0z"
          />
        </svg>
      </div>
      <div class="w-full max-lg:overflow-x-auto">
        <table class="w-full max-lg:min-w-[760px] border-collapse bg-white rounded-xl overflow-hidden shadow-sm">
          <caption class="caption-top text-left pb-2 text-[0.78rem] font-semibold text-[#1e293b]">
            Monthly income
          </caption>
          <thead>
            <tr>
              <th>No.</th>
              <th>From</th>
              <th>Upto</th>
              <th>Tax Rate</th>
              <th>Deduction</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(row, idx) in filteredRows"
              :key="row.id"
              class="hover:bg-slate-100/50 transition-colors border-b"
            >
              <td>{{ idx + 1 }}</td>
              <td>{{ formatNumber(row.from) }}</td>
              <td>{{ formatNumber(row.upto) }}</td>
              <td>{{ formatNumber(row.tax) }}%</td>
              <td>{{ row.deduction || '-' }}</td>
              <td class="actions">
                <button
                  type="button"
                  :disabled="!canEditIncomeTax"
                  @click="startEdit(row)"
                >
                  Edit
                </button>
                <label
                  class="toggle-switch"
                  :class="{ 'toggle-switch--disabled': !canEditIncomeTax }"
                  title="Status"
                >
                  <input
                    type="checkbox"
                    :checked="row.enabled"
                    :disabled="!canEditIncomeTax"
                    @change="toggleEnabled(row)"
                  >
                  <span class="slider"></span>
                </label>
              </td>
            </tr>
            <tr v-if="filteredRows.length === 0">
              <td colspan="6" style="text-align:center; color:#94a3b8; padding: 32px 15px; font-style: italic;">
                {{ emptyTableText }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, defineProps, onMounted, reactive, ref } from 'vue'
import { useStore } from 'vuex'
import FinanceToast from '../FinanceToast.vue'
import { apiFetch } from '../../../utils/api'

const props = defineProps({
  apiBase: {
    type: String,
    default: '/finance'
  },
  skipPrivilegeCheck: {
    type: Boolean,
    default: false
  },
  hideHeader: {
    type: Boolean,
    default: false
  },
  privilegeName: {
    type: String,
    default: 'Income Tax'
  }
})

const store = useStore()

const incomeTaxApiPath = computed(() => `${props.apiBase}/income-tax`)

const currentUser = computed(() => store.getters.currentUser || store.state.user || {})

const userPrivileges = computed(() => {
  const raw = currentUser.value?.privilege
  if (!raw) return {}
  if (typeof raw === 'string') {
    try {
      return JSON.parse(raw)
    } catch {
      return {}
    }
  }
  return raw
})

const hasAllPrivilege = computed(() => {
  return userPrivileges.value?.All === true || userPrivileges.value?.all === true
})

function getPrivilegeEntry(key) {
  const priv = userPrivileges.value || {}
  if (!key) return null
  if (priv?.[key]) return priv[key]

  const normalizedNeedle = String(key).trim().toLowerCase()
  const foundKey = Object.keys(priv).find((k) => String(k).trim().toLowerCase() === normalizedNeedle)
  return foundKey ? priv[foundKey] : null
}

function getActionFlag(entry, action) {
  if (!entry || typeof entry !== 'object') return false
  if (!action) return false
  if (entry[action] !== undefined) return !!entry[action]

  const a = String(action)
  const candidates = [
    a.toLowerCase(),
    a.toUpperCase(),
    a.charAt(0).toUpperCase() + a.slice(1).toLowerCase()
  ]
  for (const k of candidates) {
    if (entry[k] !== undefined) return !!entry[k]
  }
  return false
}

const canAddIncomeTax = computed(() => {
  if (props.skipPrivilegeCheck) return true
  if (hasAllPrivilege.value) return true
  return getActionFlag(getPrivilegeEntry(props.privilegeName), 'Add')
})

const canEditIncomeTax = computed(() => {
  if (props.skipPrivilegeCheck) return true
  if (hasAllPrivilege.value) return true
  return getActionFlag(getPrivilegeEntry(props.privilegeName), 'Edit')
})

const resolvedCompanyName = computed(() => {
  const user = currentUser.value
  return (
    user?.company?.name ||
    user?.companyName ||
    user?.company_name ||
    user?.company?.company_name ||
    'Company Name'
  )
})

const rows = ref([])
const isLoadingRows = ref(false)
const isSaving = ref(false)
const editingId = ref(null)
const searchQuery = ref('')

const form = reactive({
  from: '',
  upto: '',
  tax: '',
  deduction: ''
})

const toast = reactive({
  show: false,
  message: '',
  variant: 'success'
})
let toastTimer = null

function showToast(message, variant = 'success') {
  if (toastTimer) {
    window.clearTimeout(toastTimer)
    toastTimer = null
  }
  toast.message = message
  toast.variant = variant
  toast.show = true
  const duration = variant === 'error' ? 5000 : 3000
  toastTimer = window.setTimeout(() => {
    toast.show = false
    toast.variant = 'success'
    toastTimer = null
  }, duration)
}

function formatNumber(val) {
  const n = Number(val || 0)
  return Number.isFinite(n) ? n.toFixed(2) : '0.00'
}

function rowMatchesSearch(row, query) {
  const q = String(query || '').trim().toLowerCase()
  if (!q) return true
  const fromStr = formatNumber(row.from).toLowerCase()
  const uptoStr = formatNumber(row.upto).toLowerCase()
  return fromStr.includes(q) || uptoStr.includes(q)
}

const filteredRows = computed(() => {
  const q = searchQuery.value
  return rows.value.filter((row) => rowMatchesSearch(row, q))
})

const emptyTableText = computed(() => {
  const q = String(searchQuery.value || '').trim()
  if (q && rows.value.length > 0) return 'No rows match your search.'
  return 'No income tax entries saved.'
})

function resetForm() {
  form.from = ''
  form.upto = ''
  form.tax = ''
  form.deduction = ''
  editingId.value = null
}

async function loadRows() {
  isLoadingRows.value = true
  try {
    const res = await apiFetch(incomeTaxApiPath.value)
    if (!res.ok) {
      const err = await res.json().catch(() => ({}))
      showToast(err.message || 'Failed to load income tax.', 'error')
      rows.value = []
      return
    }
    const data = await res.json().catch(() => [])
    rows.value = Array.isArray(data) ? data : []
  } catch {
    showToast('Failed to load income tax.', 'error')
    rows.value = []
  } finally {
    isLoadingRows.value = false
  }
}

async function save() {
  if (isSaving.value) return
  if (editingId.value != null) {
    if (!canEditIncomeTax.value) return
  } else if (!canAddIncomeTax.value) {
    return
  }
  const from = Number(form.from)
  const upto = Number(form.upto)
  const tax = Number(form.tax)
  if (!Number.isFinite(from) || !Number.isFinite(upto) || !Number.isFinite(tax)) return

  const deduction = String(form.deduction || '').trim()
  const body = { from, upto, tax, deduction }

  isSaving.value = true
  try {
    if (editingId.value != null) {
      const res = await apiFetch(`${incomeTaxApiPath.value}/${editingId.value}`, {
        method: 'PUT',
        body: JSON.stringify(body)
      })
      if (!res.ok) {
        const err = await res.json().catch(() => ({}))
        showToast(err.message || 'Failed to update income tax.', 'error')
        return
      }
      showToast('Income tax updated successfully.', 'success')
      resetForm()
      await loadRows()
      return
    }

    const res = await apiFetch(incomeTaxApiPath.value, {
      method: 'POST',
      body: JSON.stringify(body)
    })
    if (!res.ok) {
      const err = await res.json().catch(() => ({}))
      showToast(err.message || 'Failed to save income tax.', 'error')
      return
    }
    showToast('Income tax saved successfully.', 'success')
    resetForm()
    await loadRows()
  } finally {
    isSaving.value = false
  }
}

function startEdit(row) {
  if (!canEditIncomeTax.value) return
  editingId.value = row.id
  form.from = row.from
  form.upto = row.upto
  form.tax = row.tax
  form.deduction = row.deduction != null ? String(row.deduction) : ''
}

async function toggleEnabled(row) {
  if (!canEditIncomeTax.value) return
  const nextEnabled = !row.enabled
  try {
    const res = await apiFetch(`${incomeTaxApiPath.value}/${row.id}/status`, {
      method: 'PATCH',
      body: JSON.stringify({ enabled: nextEnabled })
    })
    if (!res.ok) {
      const err = await res.json().catch(() => ({}))
      showToast(err.message || 'Failed to update status.', 'error')
      return
    }
    const idx = rows.value.findIndex((r) => r.id === row.id)
    if (idx >= 0) rows.value[idx] = { ...rows.value[idx], enabled: nextEnabled }
  } catch {
    showToast('Failed to update status.', 'error')
  }
}

onMounted(() => {
  loadRows()
})
</script>

<style scoped>
table {
  font-family: 'Segoe UI', sans-serif;
  font-size: 0.8rem;
  color: #1e293b;
}

th,
td {
  padding: 6px 8px;
  text-align: left;
  border-bottom: 1px solid #e2e8f0;
  white-space: nowrap;
  font-size: 0.7rem;
}

th {
  background: #f1f5f9;
  font-weight: 600;
}

tbody tr:hover {
  background: #f1f5f9;
}

.actions {
  display: flex;
  align-items: center;
  gap: 12px;
}

.actions button {
  background: none;
  border: none;
  color: #2563eb;
  cursor: pointer;
  text-decoration: underline;
  font-size: 0.85rem;
  padding: 0;
  display: flex;
  align-items: center;
}

.actions button:disabled {
  color: #94a3b8;
  cursor: not-allowed;
  text-decoration: none;
}

.toggle-switch {
  position: relative;
  display: inline-block;
  width: 28px;
  height: 15px;
  cursor: pointer;
}

.toggle-switch--disabled {
  cursor: not-allowed;
  opacity: 0.5;
}

.toggle-switch input {
  opacity: 0;
  width: 0;
  height: 0;
}

.slider {
  position: absolute;
  inset: 0;
  background: #cbd5e1;
  border-radius: 34px;
  transition: 0.2s;
}

.slider::before {
  content: '';
  position: absolute;
  width: 10px;
  height: 10px;
  left: 2px;
  top: 2.5px;
  background: white;
  border-radius: 50%;
  transition: 0.2s;
}

.toggle-switch input:checked + .slider {
  background: #22c55e;
}

.toggle-switch input:not(:checked) + .slider {
  background: #ef4444;
}

.toggle-switch input:checked + .slider::before {
  transform: translateX(13px);
}
</style>
